import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronDown,
  Copy,
  Download,
  LogOut,
  Mic,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  SendHorizontal,
  Trash2,
  User,
  Crown,
  GraduationCap,
  Volume2,
  Square,
  PanelLeft,
  X,
} from 'lucide-react';
import { Response } from '../components/ai/response';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MIN_SIDEBAR_WIDTH = 260;
const MAX_SIDEBAR_WIDTH = 420;

const ROUTE_TEST_CASES = [
  {
    route: 'pasal',
    label: 'Pasal',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    queries: [
      'Apa isi Pasal 21 UU Perlindungan Anak?',
      'Jelaskan Ayat (1) Pasal 14 UU ini.',
    ],
  },
  {
    route: 'topik',
    label: 'Topik',
    color: 'bg-slate-50 text-slate-700 border-slate-200',
    queries: [
      'Apa saja batas wilayah Gorontalo?',
      'Apa pengaturan umum tentang kewenangan pemerintah daerah?',
    ],
  },
  {
    route: 'perbandingan',
    label: 'Perbandingan',
    color: 'bg-violet-50 text-violet-700 border-violet-200',
    queries: [
      'Apa perbedaan antara pidana pokok dan pidana tambahan?',
      'Bandingkan kewenangan pemerintah pusat dan daerah.',
    ],
  },
  {
    route: 'wilayah',
    label: 'Wilayah',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    queries: [
      'Bagaimana batas wilayah Provinsi Gorontalo?',
      'Apa dasar pembentukan wilayah kabupaten ini?',
    ],
  },
  {
    route: 'pidana',
    label: 'Pidana',
    color: 'bg-rose-50 text-rose-700 border-rose-200',
    queries: [
      'Apa ancaman pidana untuk pelanggaran ini?',
      'Bagaimana unsur tindak pidana yang diatur?',
    ],
  },
  {
    route: 'administrasi',
    label: 'Administrasi',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    queries: [
      'Bagaimana prosedur izin administrasi ini?',
      'Apa sanksi administratif yang diatur?',
    ],
  },
  {
    route: 'uji_materi',
    label: 'Uji Materi',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    queries: [
      'Bagaimana dasar uji materi UU ini di Mahkamah Konstitusi?',
      'Apa objek pengujian norma dalam putusan MK?',
    ],
  },
];


function UserChatHome({ user, setUser }) {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const isUserLoggedIn = Boolean(user && user.role === 'USER');

  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [model, setModel] = useState('KHUSUS');
  const [isLoading, setIsLoading] = useState(false);

  const [historyMenuId, setHistoryMenuId] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [isChatLoading, setIsChatLoading] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);

  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [audioPlayingMessageId, setAudioPlayingMessageId] = useState(null);
  const [isGoogleLoginLoading, setIsGoogleLoginLoading] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
  });

  const [renameDialog, setRenameDialog] = useState({
    open: false,
    sessionId: null,
    value: '',
  });
  const [routeExamplesDialog, setRouteExamplesDialog] = useState(false);

  const audioObjectRef = useRef(null);
  const speechUtteranceRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const messagesEndRef = useRef(null);
  const composerRef = useRef(null);
  const copyTimerRef = useRef(null);

  const canSend = isUserLoggedIn && input.trim().length > 0 && !isLoading;

  const handleUseTestQuery = (query) => {
    setInput(query);
    requestAnimationFrame(autoResizeComposer);
    composerRef.current?.focus();
  };

  const getRouteBadgeClass = (route) => {
    const item = ROUTE_TEST_CASES.find((entry) => entry.route === route);
    return item?.color || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getSubQuestionBadgeClass = () => 'bg-emerald-50 text-emerald-700 border-emerald-200';


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      setSessions([]);
      setActiveSession(null);
      setMessages([]);
      setPlan(null);
      setIsSidebarOpen(false);
      return;
    }

    setIsSidebarOpen(true);
    fetchSessions();
    fetchCurrentPlan();
  }, [isUserLoggedIn]);

  // 1. Synchronize activeSession with sessionId from URL
  useEffect(() => {
    if (!isUserLoggedIn) return;
    
    if (sessionId && sessionId !== 'undefined') {
      const found = sessions.find(s => String(s.id) === String(sessionId));
      if (found) {
        if (activeSession?.id !== found.id || !activeSession.session_name) {
          setActiveSession(found);
        }
      } else {
        if (String(activeSession?.id) !== String(sessionId)) {
          setActiveSession({ id: sessionId });
        }
      }
    } else if (sessionId === 'undefined') {
      navigate('/chat', { replace: true });
    } else {
      setActiveSession(null);
    }
  }, [sessionId, isUserLoggedIn, sessions]);

  // 2. Separate Effect to fetch messages when sessionId changes
  useEffect(() => {
    if (isUserLoggedIn && sessionId && sessionId !== 'undefined') {
      fetchSessionMessages(sessionId);
    } else if (!sessionId) {
      setMessages([]);
    }
  }, [sessionId, isUserLoggedIn]);

  useEffect(() => {
    const handleClickOutside = () => {
      setHistoryMenuId(null);
      setProfileMenuOpen(false);
    };

    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isResizingSidebar) return undefined;

    const onMove = (event) => {
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
      }

      resizeFrameRef.current = requestAnimationFrame(() => {
        const nextWidth = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, event.clientX));
        setSidebarWidth(nextWidth);
      });
    };

    const onUp = () => setIsResizingSidebar(false);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [isResizingSidebar]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      stopPlayback();
    };
  }, []);

  const greetingText = useMemo(() => {
    if (isUserLoggedIn) {
      return 'What can I help with?';
    }
    return 'Login menggunakan Google untuk mulai chat.';
  }, [isUserLoggedIn]);

  const autoResizeComposer = () => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 120);
    el.style.height = `${Math.max(48, next)}px`;
    el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/chat/sessions`, { withCredentials: true });
      const list = Array.isArray(res.data) ? res.data : [];

      setSessions(list);
    } catch {
      setSessions([]);
      setActiveSession(null);
      setMessages([]);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/user/current-plan`, { withCredentials: true });
      setPlan(res.data?.data || null);
    } catch {
      setPlan(null);
    }
  };

  const fetchSessionMessages = async (sessionId) => {
    setIsChatLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/chat/session/${sessionId}`, { withCredentials: true });
      setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
    } catch {
      setMessages([]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const createSessionIfNeeded = async () => {
    if (!isUserLoggedIn) return null;
    if (activeSession?.id) return activeSession;

    const res = await axios.post(
      `${API_BASE_URL}/chat/session`,
      { session_name: 'Chat Baru' },
      { withCredentials: true }
    );

    const created = res.data;
    setSessions((prev) => [created, ...prev]);
    // setActiveSession(created); // Let the URL sync handle this
    // setMessages([]);
    navigate(`/chat/${created.id}`);
    return created;
  };

  const handleInputChange = (value) => {
    setInput(value);
    requestAnimationFrame(autoResizeComposer);
  };

  const handleSend = async () => {
    if (!canSend) return;

    const question = input.trim();
    setInput('');
    setIsLoading(true);

    if (composerRef.current) {
      composerRef.current.style.height = '48px';
      composerRef.current.style.overflowY = 'hidden';
    }

    try {
      const session = await createSessionIfNeeded();
      if (!session?.id) return;

      const userMsgId = `tmp-u-${Date.now()}`;
      setMessages((prev) => [...prev, { id: userMsgId, role: 'user', message: question, type: 'text' }]);

      const res = await axios.post(
        `${API_BASE_URL}/chat/generate`,
        {
          sessionId: session.id,
          question,
          model,
        },
        { withCredentials: true }
      );

      const botMsgId = `tmp-b-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: botMsgId,
          role: 'bot',
          message: res.data?.answer || 'Tidak ada jawaban.',
          type: 'text',
          debug: res.data?.debug || null,
        },
      ]);

      await fetchSessions();
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: `tmp-e-${Date.now()}`, role: 'bot', message: 'Gagal memproses chat.', type: 'text' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const openRenameDialog = (session) => {
    setRenameDialog({
      open: true,
      sessionId: session.id,
      value: session.session_name || '',
    });
  };

  const submitRenameDialog = async () => {
    const nextName = renameDialog.value.trim();
    if (!nextName || !renameDialog.sessionId) return;

    try {
      await axios.patch(
        `${API_BASE_URL}/chat/session/${renameDialog.sessionId}`,
        { session_name: nextName },
        { withCredentials: true }
      );

      setRenameDialog({ open: false, sessionId: null, value: '' });
      await fetchSessions();
    } catch {
      window.alert('Gagal mengubah nama percakapan.');
    }
  };

  const stopPlayback = () => {
    if (audioObjectRef.current) {
      audioObjectRef.current.pause();
      audioObjectRef.current.currentTime = 0;
      audioObjectRef.current = null;
    }

    if (speechUtteranceRef.current) {
      window.speechSynthesis.cancel();
      speechUtteranceRef.current = null;
    }

    setAudioPlayingMessageId(null);
  };

  const handleCopy = async (messageId, text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedMessageId(messageId);

      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopiedMessageId(null);
      }, 3000);
    } catch {
      // noop
    }
  };

  const handlePlayAudio = (msg, messageId) => {
    if (audioPlayingMessageId === messageId) {
      stopPlayback();
      return;
    }

    stopPlayback();

    const inlineAudio = msg.audio;
    const audioUrl = msg.audioUrl
      ? `${API_BASE_URL}/${String(msg.audioUrl).replace(/^\/+/, '')}`
      : null;

    if (inlineAudio || audioUrl) {
      const audio = new Audio(inlineAudio || audioUrl);
      audioObjectRef.current = audio;
      setAudioPlayingMessageId(messageId);

      audio.onended = () => {
        if (audioObjectRef.current === audio) {
          audioObjectRef.current = null;
          setAudioPlayingMessageId(null);
        }
      };

      audio.play().catch(() => {
        setAudioPlayingMessageId(null);
      });
      return;
    }

    if (window.speechSynthesis && msg.message) {
      const utterance = new SpeechSynthesisUtterance(msg.message);
      utterance.lang = 'id-ID';
      speechUtteranceRef.current = utterance;
      setAudioPlayingMessageId(messageId);

      utterance.onend = () => {
        speechUtteranceRef.current = null;
        setAudioPlayingMessageId(null);
      };

      window.speechSynthesis.speak(utterance);
    }
  };

  const handleExportSession = async (session) => {
    if (!session?.id) return;

    try {
      const res = await axios.get(`${API_BASE_URL}/chat/session/${session.id}/export`, {
        withCredentials: true,
        responseType: 'blob',
      });

      const blob = new Blob([res.data], { type: 'text/plain;charset=utf-8' });
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = href;
      link.download = `${(session.session_name || 'percakapan').replace(/[^a-zA-Z0-9-_ ]/g, '') || 'percakapan'}.txt`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
    } catch {
      window.alert('Gagal export chat history.');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch {
      // noop
    }

    if (typeof setUser === 'function') {
      setUser(null);
    }

    setSessions([]);
    setActiveSession(null);
    setMessages([]);
    window.location.href = '/';
  };

  const openDeleteConfirm = (session) => {
    setConfirmDialog({
      open: true,
      title: 'Hapus history chat?',
      message: `History "${session.session_name}" akan ditandai sebagai dihapus.`,
      action: async () => {
        await axios.post(`${API_BASE_URL}/chat/session/${session.id}/soft-delete`, {}, { withCredentials: true });

        if (String(activeSession?.id) === String(session.id)) {
          setActiveSession(null);
          setMessages([]);
        }

        await fetchSessions();
      },
    });
  };

  const openLogoutConfirm = () => {
    setConfirmDialog({
      open: true,
      title: 'Logout sekarang?',
      message: 'Sesi Anda akan diakhiri.',
      action: handleLogout,
    });
  };

  const executeConfirmAction = async () => {
    if (!confirmDialog.action) return;

    try {
      await confirmDialog.action();
    } catch {
      window.alert('Aksi gagal diproses.');
    } finally {
      setConfirmDialog({ open: false, title: '', message: '', action: null });
    }
  };

  const openRouteExamplesDialog = () => {
    setRouteExamplesDialog(true);
  };

  const closeRouteExamplesDialog = () => {
    setRouteExamplesDialog(false);
  };

  const goToVoiceChat = async () => {
    if (!isUserLoggedIn || isLoading) return;

    let session = activeSession;
    if (!session?.id) {
      try {
        session = await createSessionIfNeeded();
      } catch {
        window.alert('Gagal menyiapkan sesi voice chat.');
        return;
      }
    }

    navigate('/chat/voice', {
      state: {
        sessionId: session?.id || null,
        model,
      },
    });
  };

  const startGoogleLogin = () => {
    if (isGoogleLoginLoading) return;
    setIsGoogleLoginLoading(true);
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  if (!isUserLoggedIn) {
    return (
      <div className="tw min-h-screen w-full bg-[#f7f7f8] text-[#111827]">
        {isGoogleLoginLoading ? (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/95">
            <div className="text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-neutral-300 border-t-neutral-900" />
              <p className="mt-3 text-sm font-medium text-neutral-700">Memproses login Google...</p>
            </div>
          </div>
        ) : null}
        <div className="mx-auto flex min-h-screen w-full max-w-[1800px] flex-col">
          <header className="flex h-16 items-center justify-between border-b border-[#ececf1] px-6">
            <div className="text-lg font-semibold text-neutral-900">ChatLawID</div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={startGoogleLogin}
                className="rounded-full bg-neutral-900 px-6 py-2 text-sm font-semibold text-white"
              >
                Log in
              </button>
              <button
                type="button"
                onClick={startGoogleLogin}
                className="rounded-full border border-neutral-300 bg-white px-6 py-2 text-sm font-semibold text-neutral-800"
              >
                Sign up for free!!
              </button>
            </div>
          </header>

          <main className="flex flex-1 flex-col items-center justify-center px-4 pb-24">
            <div className="mb-8 text-center">
              <div className="text-base font-semibold uppercase tracking-[0.22em] text-[#202123]">ChatLawID</div>
              <h1 className="mt-3 text-center text-5xl font-semibold tracking-tight text-neutral-900">Asisten Hukum Indonesia Berbasis AI</h1>
              <p className="mx-auto mt-4 max-w-3xl text-base text-[#6e6e80]">
                Fokus untuk riset peraturan, analisis pasal, dan diskusi hukum praktis dengan dua mode model:
                <span className="font-semibold text-[#202123]"> UMUM</span> dan
                <span className="font-semibold text-[#202123]"> KHUSUS</span>.
              </p>
            </div>

            <div className="w-full max-w-[920px] rounded-[28px] border border-[#d9d9e3] bg-white p-4 shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
              <textarea
                disabled
                placeholder="Ask anything"
                className="h-12 w-full resize-none border-none bg-transparent px-3 py-2 text-2xl placeholder:text-neutral-400 focus:outline-none"
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <GhostChip label="Search" />
                  <GhostChip label="Study" />
                </div>
                <button
                  type="button"
                  onClick={startGoogleLogin}
                  className="rounded-full bg-neutral-100 px-5 py-2 text-sm font-semibold text-neutral-800"
                >
                  Voice
                </button>
              </div>
            </div>

            <p className="mt-16 text-center text-xs text-neutral-500">
              By messaging ChatLawID, you agree to our Terms and Privacy Policy.
            </p>
          </main>
        </div>
      </div>
    );
  }

  const chatFooterLeft = isSidebarOpen ? `${sidebarWidth + 1}px` : '0px';
  const rightHeaderLeft = isSidebarOpen ? `${sidebarWidth + 1}px` : '0px';

  return (
    <div className="tw min-h-screen w-full bg-[#f7f7f8] text-[#111827]">
      <div className="flex min-h-screen w-full border-x border-[#ececf1] bg-[#f7f7f8]">
        {isSidebarOpen ? (
          <aside
            className="custom-scrollbar sticky top-0 z-20 flex h-screen shrink-0 flex-col overflow-y-auto border-r border-[#ececf1] bg-[#f7f7f8]"
            style={{ width: `${sidebarWidth}px` }}
          >
            <div className="sticky top-0 z-20 shrink-0 border-b border-[#ececf1] bg-[#f7f7f8] px-2 pb-2 pt-1.5 shadow-sm">
              <div className="flex h-10 items-center justify-between px-1">
                <div className="text-sm font-bold text-[#202123]">ChatLawID</div>
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(false)}
                  className="rounded-md p-1 text-[#6e6e80] hover:bg-[#ececf1]"
                >
                  <PanelLeft size={14} />
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  navigate('/chat');
                }}
                className="flex w-full items-center gap-2 rounded-xl border border-[#d9d9e3] bg-white px-3 py-1.5 text-left text-sm font-medium text-[#202123] hover:bg-[#f7f7f8]"
              >
                <Plus size={14} />
                New chat
              </button>
              <button
                type="button"
                className="mt-1.5 flex w-full items-center gap-2 rounded-xl px-3 py-1.5 text-left text-sm text-[#6e6e80] hover:bg-[#ececf1]"
              >
                <Search size={14} />
                Search chats
              </button>
            </div>

            <div className="mb-2 shrink-0 px-3 pt-5 text-[10px] font-bold uppercase tracking-widest text-[#8e8ea0]">Your chats</div>

            <div className="px-2 pb-4">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => {
                    navigate(`/chat/${session.id}`);
                    setHistoryMenuId(null);
                  }}
                  className={`mb-[2px] cursor-pointer rounded-lg px-0.5 py-0.5 ${String(activeSession?.id) === String(session.id) ? 'bg-[#ececf1]' : 'hover:bg-[#ececf1]'
                    }`}
                >
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="line-clamp-1 flex-1 rounded-md px-2 py-1 text-left text-sm text-[#202123]"
                    >
                      {session.session_name}
                    </button>

                    <div className="relative">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setHistoryMenuId((prev) => (prev === session.id ? null : session.id));
                        }}
                        className="rounded-md p-1 text-[#6e6e80] hover:bg-[#d9d9e3]"
                      >
                        <MoreHorizontal size={14} />
                      </button>

                      {historyMenuId === session.id ? (
                        <div
                          className="absolute right-0 top-8 z-20 w-44 rounded-xl border border-[#d9d9e3] bg-white p-1 shadow-lg"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MenuAction
                            icon={<Pencil size={13} />}
                            label="Edit nama"
                            onClick={() => {
                              setHistoryMenuId(null);
                              openRenameDialog(session);
                            }}
                          />
                          <MenuAction
                            icon={<Trash2 size={13} />}
                            label="Delete history"
                            onClick={() => {
                              setHistoryMenuId(null);
                              openDeleteConfirm(session);
                            }}
                          />
                          <MenuAction
                            icon={<Download size={13} />}
                            label="Export"
                            onClick={() => {
                              setHistoryMenuId(null);
                              handleExportSession(session);
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="sticky bottom-0 z-20 mt-auto shrink-0 border-t border-[#ececf1] bg-[#f7f7f8] p-3 shadow-[0_-1px_2px_rgba(0,0,0,0.02)]"
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setProfileMenuOpen((prev) => !prev);
                  }}
                  className="flex w-full items-center justify-between rounded-xl bg-[#ececf1] px-3 py-1.5 text-left"
                >
                  <div>
                    <div className="text-sm font-semibold text-[#202123]">{user?.name || '-'}</div>
                    <div className="text-xs text-[#6e6e80]">{plan?.current_plan || 'FREE plan'}</div>
                  </div>
                  <ChevronDown size={15} className={profileMenuOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                </button>

                {profileMenuOpen ? (
                  <div
                    className="absolute bottom-14 left-0 z-20 w-full rounded-xl border border-[#d9d9e3] bg-white p-1 shadow-lg"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <MenuAction icon={<User size={13} />} label="Edit profile" href="/dashboard/profile" />
                    <MenuAction icon={<Crown size={13} />} label="Upgrade premium" href="/dashboard/pricelist" />
                    <MenuAction
                      icon={<GraduationCap size={13} />}
                      label="Examination Assistant"
                      href="/dashboard/examination-assistant"
                    />
                    <MenuAction icon={<LogOut size={13} />} label="Logout" onClick={openLogoutConfirm} />
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        ) : null}

        {isSidebarOpen ? (
          <div
            className={`relative w-1 shrink-0 cursor-col-resize ${isResizingSidebar ? 'bg-[#b8b8c7]' : 'bg-transparent hover:bg-[#d9d9e3]'}`}
            onMouseDown={(event) => {
              event.preventDefault();
              setIsResizingSidebar(true);
            }}
          >
            <div className="absolute inset-y-0 -left-2 -right-2" />
          </div>
        ) : null}

        <section className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[#f7f7f8]">
          <header
            className="fixed right-0 top-0 z-30 flex h-12 items-center justify-between border-b border-[#ececf1] bg-[#f7f7f8] px-4"
            style={{ left: rightHeaderLeft }}
          >
            <div className="flex items-center gap-3">
              {!isSidebarOpen ? (
                <button
                  type="button"
                  onClick={() => setIsSidebarOpen(true)}
                  className="rounded-md p-1 text-[#6e6e80] hover:bg-[#ececf1]"
                >
                  <PanelLeft size={16} />
                </button>
              ) : null}
              <div className="text-xl font-bold tracking-tight text-[#202123]">ChatLawID</div>
            </div>

            <button
              type="button"
              onClick={() => handleExportSession(activeSession)}
              disabled={!activeSession?.id}
              className="inline-flex items-center gap-2 rounded-lg border border-[#d9d9e3] bg-white px-3 py-1.5 text-sm font-medium text-[#202123] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Download size={14} />
              Export chat history
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-28 pt-12">
            {isChatLoading ? (
              <div className="flex h-full flex-col items-center justify-center text-center mt-20">
                <div className="mb-4 flex gap-2">
                  <div className="h-2 w-2 animate-bounce rounded-full bg-[#6e6e80] [animation-delay:-0.3s]"></div>
                  <div className="h-2 w-2 animate-bounce rounded-full bg-[#6e6e80] [animation-delay:-0.15s]"></div>
                  <div className="h-2 w-2 animate-bounce rounded-full bg-[#6e6e80]"></div>
                </div>
                <p className="text-sm text-[#6e6e80]">Memuat pesan...</p>
              </div>
            ) : (messages.length === 0 && !sessionId) ? (
              <div className="mx-auto mt-20 max-w-3xl">
                <h2 className="mb-3 text-center text-5xl font-semibold tracking-tight text-[#202123]">{greetingText}</h2>
              </div>
            ) : messages.length > 0 ? (
              <div className="mx-auto w-full max-w-4xl space-y-6 pb-20">
                {messages.map((msg, idx) => {
                  const isUser = msg.role === 'user';
                  const msgId = msg.id || `${msg.role}-${msg.created_at || idx}`;
                  const isCopied = copiedMessageId === msgId;
                  const isPlaying = audioPlayingMessageId === msgId;

                  return (
                    <div key={msgId} className="flex w-full">
                      <div
                        className={`w-full rounded-2xl px-4 py-3 text-base leading-relaxed ${isUser ? 'bg-[#ececf1] text-[#202123]' : 'bg-transparent text-[#202123]'
                          }`}
                      >
                        <Response>{msg.message}</Response>
                        {msg.role === 'bot' && msg.debug?.route ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <div className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${getRouteBadgeClass(msg.debug.route)}`}>
                              Route: {msg.debug.route}
                            </div>
                            {msg.debug?.used_subquestions ? (
                              <div className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${getSubQuestionBadgeClass()}`}>
                                Sub-Question Engine: ON
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {msg.role === 'bot' && msg.debug ? (
                          <div className="mt-3 rounded-2xl border border-dashed border-[#e5e7eb] bg-[#fafafa] p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6e6e80]">
                                Debug
                              </div>
                              <div className="text-[11px] text-[#8e8ea0]">
                                {msg.debug.used_subquestions ? 'Sub-Question aktif' : 'Sub-Question nonaktif'}
                              </div>
                            </div>

                            <div className="mt-2 grid grid-cols-1 gap-2 text-[12px] text-[#202123] sm:grid-cols-2">
                              <div className="rounded-xl border border-[#ececf1] bg-white px-3 py-2">
                                <span className="font-medium text-[#6e6e80]">Route:</span> {msg.debug.route || '-'}
                              </div>
                              <div className="rounded-xl border border-[#ececf1] bg-white px-3 py-2">
                                <span className="font-medium text-[#6e6e80]">Sub-Questions:</span> {Array.isArray(msg.debug.subquestions) ? msg.debug.subquestions.length : 0}
                              </div>
                            </div>

                            {Array.isArray(msg.debug.subquestions) && msg.debug.subquestions.length > 0 ? (
                              <ul className="mt-3 space-y-1.5 text-sm text-[#202123]">
                                {msg.debug.subquestions.map((item, subIndex) => (
                                  <li key={`${msgId}-subq-${subIndex}`} className="flex gap-2 leading-5">
                                    <span className="mt-[2px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-semibold text-emerald-700">
                                      {subIndex + 1}
                                    </span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="mt-3 flex items-center gap-3 text-xs text-[#6e6e80]">
                          <button
                            type="button"
                            onClick={() => handleCopy(msgId, msg.message)}
                            className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-black/5"
                          >
                            <Copy size={12} />
                            {isCopied ? 'Copied' : 'Copy'}
                          </button>

                          <button
                            type="button"
                            onClick={() => handlePlayAudio(msg, msgId)}
                            className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-black/5"
                          >
                            {isPlaying ? <Square size={12} /> : <Volume2 size={12} />}
                            {isPlaying ? 'Stop' : 'Play'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {isLoading ? (
                  <div className="rounded-[18px] bg-[#ececf1] px-5 py-4 text-lg text-[#6e6e80]">Thinking...</div>
                ) : null}

                <div ref={messagesEndRef} />
              </div>
            ) : null}
          </div>

          <footer
            className="fixed bottom-0 right-0 z-30 border-t border-[#ececf1] bg-[#f7f7f8] px-4 py-3 transition-[left] duration-150"
            style={{ left: chatFooterLeft }}
          >
            <div className="mx-auto w-full max-w-4xl rounded-[20px] border border-[#d9d9e3] bg-white p-3 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSend();
                }}
                className="flex items-end gap-3"
              >
                <textarea
                  ref={composerRef}
                  value={input}
                  onChange={(event) => handleInputChange(event.target.value)}
                  rows={1}
                  placeholder="Ask anything"
                  disabled={!isUserLoggedIn || isLoading}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                  className="h-11 min-h-[44px] flex-1 resize-none rounded-xl border-none bg-transparent px-2 py-2.5 text-base placeholder:text-[#8e8ea0] focus:outline-none disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={!canSend}
                  className="inline-flex h-11 items-center gap-1 rounded-xl bg-[#202123] px-4 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <SendHorizontal size={15} />
                  Send
                </button>
              </form>

              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <select
                    value={model}
                    onChange={(event) => setModel(event.target.value)}
                    disabled={!isUserLoggedIn || isLoading}
                    className="h-9 rounded-lg border border-[#d9d9e3] bg-white px-3 text-xs text-[#202123] disabled:opacity-60"
                  >
                    <option value="UMUM">UMUM</option>
                    <option value="KHUSUS">KHUSUS</option>
                  </select>

                  <button
                    type="button"
                    onClick={goToVoiceChat}
                    disabled={!isUserLoggedIn || isLoading}
                    className="inline-flex h-9 items-center gap-1 rounded-lg border border-[#d9d9e3] bg-white px-3 text-xs font-medium text-[#202123] disabled:opacity-60"
                  >
                    <Mic size={14} />
                    Voice Chat
                  </button>

                  <button
                    type="button"
                    onClick={openRouteExamplesDialog}
                    disabled={!isUserLoggedIn || isLoading}
                    className="inline-flex h-9 items-center gap-1 rounded-lg border border-[#d9d9e3] bg-white px-3 text-xs font-medium text-[#202123] disabled:opacity-60"
                  >
                    <Search size={14} />
                    Contoh Route
                  </button>
                </div>

                <div className="text-xs text-[#8e8ea0]">ChatLawID can make mistakes. Check important info.</div>
              </div>
            </div>
          </footer>

      {routeExamplesDialog ? (
        <DialogBackdrop>
          <div className="w-full max-w-5xl rounded-2xl border border-[#d9d9e3] bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-[#202123]">Contoh Route</h3>
                <p className="mt-1 text-sm text-[#6e6e80]">Pilih contoh query untuk mengecek akurasi routing dengan cepat.</p>
              </div>
              <button
                type="button"
                className="rounded-md p-1 text-[#6e6e80] hover:bg-[#ececf1]"
                onClick={closeRouteExamplesDialog}
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {ROUTE_TEST_CASES.map((item) => (
                  <div key={item.route} className="rounded-2xl border border-[#ececf1] bg-[#fafafa] p-3">
                    <div className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${item.color}`}>
                      {item.label}
                    </div>
                    <div className="mt-3 space-y-2">
                      {item.queries.map((query) => (
                        <button
                          key={query}
                          type="button"
                          onClick={() => {
                            handleUseTestQuery(query);
                            closeRouteExamplesDialog();
                          }}
                          className="block w-full rounded-xl border border-[#ececf1] bg-white px-3 py-2 text-left text-[12px] leading-5 text-[#202123] hover:border-[#d9d9e3] hover:bg-[#f8f9fa]"
                        >
                          {query}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogBackdrop>
      ) : null}

      {confirmDialog.open ? (
        <DialogBackdrop>
          <div className="w-full max-w-md rounded-2xl border border-[#d9d9e3] bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-[#202123]">{confirmDialog.title}</h3>
            <p className="mt-2 text-sm text-[#6e6e80]">{confirmDialog.message}</p>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-[#d9d9e3] bg-white px-4 py-2 text-sm font-medium text-[#202123]"
                onClick={() => setConfirmDialog({ open: false, title: '', message: '', action: null })}
              >
                Batal
              </button>
              <button
                type="button"
                className="rounded-lg bg-[#202123] px-4 py-2 text-sm font-semibold text-white"
                onClick={executeConfirmAction}
              >
                Ya, lanjut
              </button>
            </div>
          </div>
        </DialogBackdrop>
      ) : null}

      {renameDialog.open ? (
        <DialogBackdrop>
          <div className="w-full max-w-md rounded-2xl border border-[#d9d9e3] bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#202123]">Edit nama history chat</h3>
              <button
                type="button"
                className="rounded-md p-1 text-[#6e6e80] hover:bg-[#ececf1]"
                onClick={() => setRenameDialog({ open: false, sessionId: null, value: '' })}
              >
                <X size={15} />
              </button>
            </div>

            <input
              type="text"
              value={renameDialog.value}
              onChange={(event) => setRenameDialog((prev) => ({ ...prev, value: event.target.value }))}
              className="mt-4 w-full rounded-xl border border-[#d9d9e3] px-3 py-2 text-sm outline-none focus:border-[#202123]"
              placeholder="Masukkan nama percakapan"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-[#d9d9e3] bg-white px-4 py-2 text-sm font-medium text-[#202123]"
                onClick={() => setRenameDialog({ open: false, sessionId: null, value: '' })}
              >
                Batal
              </button>
              <button
                type="button"
                className="rounded-lg bg-[#202123] px-4 py-2 text-sm font-semibold text-white"
                onClick={submitRenameDialog}
              >
                Simpan
              </button>
            </div>
          </div>
        </DialogBackdrop>
      ) : null}
        </section>
      </div>
    </div>
  );
}

function GhostChip({ label }) {
  return (
    <button
      type="button"
      className="rounded-full border border-[#d9d9e3] bg-white px-4 py-2 text-sm font-medium text-[#202123]"
    >
      {label}
    </button>
  );
}

function MenuAction({ icon, label, onClick, href }) {
  const className = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-[#202123] hover:bg-[#f7f7f8]';

  if (href) {
    return (
      <a href={href} className={className}>
        {icon}
        {label}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {icon}
      {label}
    </button>
  );
}

function DialogBackdrop({ children }) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">{children}</div>;
}

export default UserChatHome;
